/**
 * Paramètres - Surprise du Jour
 * Page 5/6 du module
 * MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
 */
import React, { useState, useEffect } from 'react';
import { SurpriseDuJourTabs } from '../components/SurpriseDuJourTabs';
import { surpriseService } from '../services/firebaseService';

const SurpriseDuJourSettings = () => {
  const [settings, setSettings] = useState({
    module_active: true,
    hidden: false,  // Cacher le jeu sur l'app
    test_mode_enabled: false,  // Mode test: permet de rejouer plusieurs fois
    test_plays_per_day: 999,   // FIX Build 207: Nombre de parties en mode test
    reset_time: '00:00',
    reward_expiry_days: 7,  // FIX Build 202: Aligné avec CF (expiry, pas expiration)
    jackpot_limit_hours: 12,
    notification_jackpot_message: '🎉 Quelqu\'un vient de gagner un Menu offert ! Tourne la roue maintenant 🎰',
    notification_win_message: '🎁 Bravo ! Vous avez gagné {{reward}} !',
    allow_multiple_devices: false,
    require_device_id: true,
    log_ip_addresses: true,
    // Nouvelles règles récompenses
    min_order_amount_to_use_reward: 17,
    max_pending_rewards: 2,
    win_probability: 75,
    min_orders_to_play: 0
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      // FIREBASE DIRECT: Charger depuis settings/surprise_du_jour
      const response = await surpriseService.getConfig();
      if (response.config) {
        setSettings({ ...settings, ...response.config });
      }
      setError(null);
    } catch (err) {
      console.log('Settings not found, using defaults:', err);
      // Use default settings if not found
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // FIREBASE DIRECT: Sauvegarder dans settings/surprise_du_jour
      await surpriseService.updateConfig(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Erreur lors de la sauvegarde: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          🎰 Surprise du Jour
        </h1>
        <p className="text-gray-600">
          Gestion du module de jeu quotidien
        </p>
      </div>

      {/* Tabs Navigation */}
      <SurpriseDuJourTabs />

      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">
          ⚙️ Paramètres
        </h2>
        <p className="text-sm text-gray-600">
          Configuration générale du module Surprise du Jour
        </p>
      </div>

      {/* Error Message - reste en haut */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          ❌ {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Module Activation */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">🎮 Activation du Module</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Module Surprise du Jour</p>
                <p className="text-sm text-gray-600">Activer ou désactiver le jeu pour tous les utilisateurs</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.module_active}
                  onChange={(e) => setSettings({...settings, module_active: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Option Cacher */}
            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div>
                <p className="font-medium text-gray-700">🙈 Cacher le jeu sur l'app</p>
                <p className="text-sm text-gray-500">Le bouton sera invisible même si le jeu est activé</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.hidden || false}
                  onChange={(e) => setSettings({...settings, hidden: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gray-600"></div>
              </label>
            </div>

            {/* Mode Test */}
            <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div>
                <p className="font-medium text-orange-800">🧪 Mode Test</p>
                <p className="text-sm text-orange-600">Permet de rejouer immédiatement sans attendre minuit (pour les tests)</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.test_mode_enabled}
                  onChange={(e) => setSettings({...settings, test_mode_enabled: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>
            {settings.test_mode_enabled && (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700 font-medium">
                    ⚠️ Mode Test ACTIVÉ - N'oubliez pas de le désactiver en production !
                  </p>
                </div>
                <div className="flex items-center gap-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-orange-800">🔢 Parties par jour en mode test</p>
                    <p className="text-sm text-orange-600">Nombre de parties autorisées par jour par joueur</p>
                  </div>
                  <input
                    type="number"
                    min="1"
                    max="9999"
                    value={settings.test_plays_per_day || 999}
                    onChange={(e) => setSettings({...settings, test_plays_per_day: parseInt(e.target.value) || 999})}
                    className="w-24 border border-orange-300 rounded px-3 py-2 text-center font-bold text-orange-800"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Timing */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">⏰ Configuration Temporelle</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Heure de réinitialisation quotidienne</label>
              <input
                type="time"
                value={settings.reset_time}
                onChange={(e) => setSettings({...settings, reset_time: e.target.value})}
                className="border rounded px-3 py-2 w-48"
              />
              <p className="text-sm text-gray-600 mt-1">Par défaut: Minuit (00:00)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Durée de validité des récompenses (jours)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={settings.reward_expiry_days}
                onChange={(e) => setSettings({...settings, reward_expiry_days: parseInt(e.target.value)})}
                className="border rounded px-3 py-2 w-48"
              />
              <p className="text-sm text-gray-600 mt-1">Les récompenses expirent après ce délai</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Délai minimum entre jackpots (heures)</label>
              <input
                type="number"
                min="1"
                max="48"
                value={settings.jackpot_limit_hours}
                onChange={(e) => setSettings({...settings, jackpot_limit_hours: parseInt(e.target.value)})}
                className="border rounded px-3 py-2 w-48"
              />
              <p className="text-sm text-gray-600 mt-1">Empêche les utilisateurs d'obtenir plusieurs jackpots trop rapidement</p>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">🔔 Messages de Notifications</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Message jackpot (notification globale)</label>
              <textarea
                value={settings.notification_jackpot_message}
                onChange={(e) => setSettings({...settings, notification_jackpot_message: e.target.value})}
                className="w-full border rounded px-3 py-2"
                rows="2"
              />
              <p className="text-sm text-gray-600 mt-1">Affiché à tous quand quelqu'un gagne un jackpot</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Message victoire (notification individuelle)</label>
              <textarea
                value={settings.notification_win_message}
                onChange={(e) => setSettings({...settings, notification_win_message: e.target.value})}
                className="w-full border rounded px-3 py-2"
                rows="2"
              />
              <p className="text-sm text-gray-600 mt-1">
                Variables disponibles: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{'{{reward}}'}</code>, <code className="bg-gray-100 px-2 py-1 rounded text-xs">{'{{expiration}}'}</code>
              </p>
            </div>
          </div>
        </div>

        {/* Règles Récompenses */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">🎁 Règles des Récompenses</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Probabilité de gain (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={settings.win_probability || 75}
                onChange={(e) => setSettings({...settings, win_probability: parseInt(e.target.value)})}
                className="border rounded px-3 py-2 w-48"
              />
              <p className="text-sm text-gray-600 mt-1">Pourcentage de chance de gagner (75% = 3 chances sur 4)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Commandes minimum pour jouer</label>
              <input
                type="number"
                min="0"
                max="100"
                value={settings.min_orders_to_play || 0}
                onChange={(e) => setSettings({...settings, min_orders_to_play: parseInt(e.target.value)})}
                className="border rounded px-3 py-2 w-48"
              />
              <p className="text-sm text-gray-600 mt-1">Nombre de commandes nécessaires pour débloquer le jeu (0 = pas de minimum)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Montant minimum pour utiliser une récompense (€)</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={settings.min_order_amount_to_use_reward || 0}
                onChange={(e) => setSettings({...settings, min_order_amount_to_use_reward: parseFloat(e.target.value)})}
                className="border rounded px-3 py-2 w-48"
              />
              <p className="text-sm text-gray-600 mt-1">Montant minimum de commande pour utiliser une récompense (0 = pas de minimum)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Maximum de récompenses en attente</label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.max_pending_rewards || 2}
                onChange={(e) => setSettings({...settings, max_pending_rewards: parseInt(e.target.value)})}
                className="border rounded px-3 py-2 w-48"
              />
              <p className="text-sm text-gray-600 mt-1">Nombre maximum de récompenses non utilisées par client (empêche le stockage)</p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Règle non cumulable :</strong> Un client ne peut utiliser qu'<strong>une seule récompense par commande</strong>.
                Cette règle est appliquée automatiquement dans l'app.
              </p>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">🔒 Sécurité et Anti-Triche</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Autoriser plusieurs appareils</p>
                <p className="text-sm text-gray-600">Permettre à un utilisateur de jouer depuis différents appareils</p>
              </div>
              <input
                type="checkbox"
                checked={settings.allow_multiple_devices}
                onChange={(e) => setSettings({...settings, allow_multiple_devices: e.target.checked})}
                className="h-5 w-5"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Exiger l'ID d'appareil</p>
                <p className="text-sm text-gray-600">Obligation de fournir un device_id pour jouer</p>
              </div>
              <input
                type="checkbox"
                checked={settings.require_device_id}
                onChange={(e) => setSettings({...settings, require_device_id: e.target.checked})}
                className="h-5 w-5"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Enregistrer les adresses IP</p>
                <p className="text-sm text-gray-600">Tracker les IPs pour détecter les abus</p>
              </div>
              <input
                type="checkbox"
                checked={settings.log_ip_addresses}
                onChange={(e) => setSettings({...settings, log_ip_addresses: e.target.checked})}
                className="h-5 w-5"
              />
            </div>
          </div>
        </div>

        {/* Save Button + Success Message */}
        <div className="flex justify-end items-center gap-4">
          {saved && (
            <span className="text-green-600 font-medium">✅ Sauvegardé !</span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
          >
            {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder les paramètres'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurpriseDuJourSettings;
